import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { formatDate, loadPreferences, savePreferences, type Preferences } from '@/lib/preferences';

interface PreferencesContextValue {
  readonly preferences: Preferences;
  /** Applies at once and saves; returns false when the browser refused to store it. */
  readonly setPreferences: (preferences: Preferences) => boolean;
  /** A `YYYY-MM-DD` date in the reader's chosen format. */
  readonly date: (date: string) => string;
}

const PreferencesContext = createContext<PreferencesContextValue | undefined>(undefined);

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const [preferences, setState] = useState<Preferences>(loadPreferences);
  const setPreferences = useCallback((next: Preferences) => {
    setState(next);
    return savePreferences(next);
  }, []);
  const value = useMemo<PreferencesContextValue>(
    () => ({ preferences, setPreferences, date: (date: string) => formatDate(date, preferences.dateFormat) }),
    [preferences, setPreferences],
  );
  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences(): PreferencesContextValue {
  const value = useContext(PreferencesContext);
  if (value === undefined) {
    throw new Error('usePreferences is used outside <PreferencesProvider>. Wrap the app in it.');
  }
  return value;
}
